import * as m from '@/paraglide/messages';
import type { NoteRow as Note } from '@listening/frontend/query/notes';
import { cn } from '@listening/utils/react-helpers';
import ChevronDownIcon from '@listening/assets/icons/chevronDown.svg?react';

import { Button } from '@listening/frontend/components/radix/button';
import { DialogContent } from '@listening/frontend/components/radix/dialog';
import { DialogCloseContext } from '@listening/frontend/components/custom/dialog-dropdown';
import {
  ScrollArea,
  ScrollBar,
} from '@listening/frontend/components/radix/scroll-area';
import { Separator } from '@listening/frontend/components/radix/separator';
import { Switch } from '@listening/frontend/components/radix/switch';
import { Toggle } from '@listening/frontend/components/radix/toggle';
import { format } from 'date-fns';
import React, { use, useCallback, useEffect, useMemo, useState } from 'react';

type ExportFormat = 'CSV' | 'TXT' | 'PDF';

function formatMilliseconds(ms: number) {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

function formatNoteLocation(note: Note) {
  // doc-pack notes and legacy audio-offset notes are mutually exclusive anchors, but
  // an doc-pack note can legitimately carry chapter_audio_offset_ms === 0 (a real
  // offset, not "absent"). So check the doc-pack anchor FIRST — otherwise a `!== null`
  // audio-offset test would shadow an doc-pack note that happens to sit at offset 0
  // and render it as a 00:00 timestamp instead of its page/chars.
  if (note.doc_pack_page !== null && note.doc_pack_char_start !== null) {
    const charEnd =
      note.doc_pack_char_end !== null
        ? `-${note.doc_pack_char_end.toString()}`
        : '';
    return `Page ${note.doc_pack_page.toString()}, chars ${note.doc_pack_char_start.toString()}${charEnd}`;
  }
  if (note.chapter_audio_offset_ms !== null) {
    return formatMilliseconds(note.chapter_audio_offset_ms);
  }
  return '';
}

function FormatToggle({
  format,
  selectedFormat,
  setSelected,
}: {
  format: ExportFormat;
  selectedFormat: ExportFormat;
  setSelected: (f: ExportFormat) => void;
}) {
  return (
    <Toggle
      className='flex items-center justify-center rounded-lg border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] p-[6px] text-base font-medium text-[var(--v2-text-muted)] data-[state=on]:bg-[var(--v2-accent)]/10'
      onPressedChange={(val) => {
        if (val) {
          setSelected(format);
        }
      }}
      pressed={selectedFormat === format}
    >
      {format}
    </Toggle>
  );
}

type PossibleColumn =
  | 'Title'
  | 'Body'
  | 'Created At'
  | 'Updated At'
  // | 'Paragraph'
  // | 'Page'
  | 'Timestamp';
export function ExportNotesDialog({ notes }: { notes: ReadonlyArray<Note> }) {
  const closeContext = use(DialogCloseContext);
  const closeDialog = closeContext.closeDialog;

  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('CSV');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const [columnPreferences, setColumnPreferences] = useState<
    Record<PossibleColumn, boolean>
  >({
    Title: true,
    Body: true,
    'Created At': true,
    'Updated At': true,
    Timestamp: true,
    // Paragraph: true,
    // Page: false,
  });

  const enabledColumns = useMemo(
    () =>
      new Set(
        Object.keys(columnPreferences).filter(
          (k) => columnPreferences[k as PossibleColumn],
        ),
      ) as Set<PossibleColumn>,
    [columnPreferences],
  );

  const escapeCsvValue = useCallback((value: string): string => {
    if (value.includes(',') || value.includes('\n') || value.includes('"')) {
      return `"${value.replaceAll('"', '""')}"`;
    }
    return value;
  }, []);

  const { notesWithMetaData } = useNotesWithMetadata(notes, enabledColumns);

  const formattedBody = useMemo(() => {
    if (selectedFormat === 'CSV') {
      const header = Array.from(enabledColumns)
        .map((k) => escapeCsvValue(k))
        .join(',');
      const body = notesWithMetaData
        .map((note) =>
          Object.values(note)
            .map((v) => escapeCsvValue(v))
            .join(','),
        )
        .join('\n');
      return `${header}\n${body}`;
    }
    if (selectedFormat === 'TXT') {
      return notesWithMetaData
        .map((note) =>
          Object.entries(note)
            .map(([k, v]) => `${k}: ${v}`)
            .join('\n'),
        )
        .join('\n\n');
    }
    return '';
  }, [enabledColumns, notesWithMetaData, selectedFormat, escapeCsvValue]);

  return (
    <DialogContent className='flex h-min max-h-[90svh] max-w-160 flex-col gap-5 p-6 pt-8'>
      <div className='flex justify-between'>
        <p className='text-lg font-semibold'>{m.dialog_export()}</p>
        <Button
          className='rounded-lg'
          onClick={() => {
            const file = new Blob([formattedBody], { type: 'text/plain' });

            const a = document.createElement('a'),
              url = URL.createObjectURL(file);
            a.href = url;

            const filename = `notes-export.${selectedFormat.toLowerCase()}`;
            a.download = filename;
            document.body.append(a);
            a.click();
            setTimeout(() => {
              a.remove();
              window.URL.revokeObjectURL(url);
            }, 0);
            setTimeout(() => {
              closeDialog();
            }, 150);
          }}
        >
          {m.dialog_export()}
        </Button>
      </div>
      <ScrollArea>
        <div className='flex flex-col gap-8 px-2'>
          <div className='flex flex-col gap-3'>
            <p className='text-base leading-normal font-medium tracking-tight text-[var(--v2-text-muted)]'>
              {m.dialog_format()}
            </p>
            <ScrollArea>
              <div className='flex gap-3'>
                {/* <FormatToggle
                format="PDF"
                selectedFormat={selectedFormat}
                setSelected={setSelectedFormat}
              /> */}
                <FormatToggle
                  format='CSV'
                  selectedFormat={selectedFormat}
                  setSelected={setSelectedFormat}
                />
                <FormatToggle
                  format='TXT'
                  selectedFormat={selectedFormat}
                  setSelected={setSelectedFormat}
                />
              </div>
              <ScrollBar orientation='horizontal' />
            </ScrollArea>
          </div>
          <Separator />
          <ScrollArea
            className='max-h-64 min-h-32 grow rounded-lg bg-[var(--v2-card-bg)] p-4'
            useBlockFix
          >
            <p>
              {formattedBody.split('\n').map((line, index) => (
                <React.Fragment key={`fragment-${index.toString()}`}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </p>
          </ScrollArea>
          <Button
            className='flex gap-2 self-center'
            onClick={() => {
              setShowAdvancedOptions(!showAdvancedOptions);
            }}
            variant='ghost'
          >
            <p>{m.dialog_advanced_options()}</p>
            <ChevronDownIcon
              className={cn(showAdvancedOptions && 'rotate-180')}
            />
          </Button>
          {showAdvancedOptions && (
            <div className='flex flex-col gap-4'>
              {/* <div className="flex gap-4">
                <p>Include Page</p>
                <Switch
                  onCheckedChange={(v) => {
                    setColumnPreferences({ ...columnPreferences, Page: v });
                  }}
                  checked={columnPreferences.Page}
                />
              </div> */}
              {/* <div className="flex gap-4">
                <p>Include Paragraph</p>
                <Switch
                  onCheckedChange={(v) => {
                    setColumnPreferences({
                      ...columnPreferences,
                      Paragraph: v,
                    });
                  }}
                  checked={columnPreferences.Paragraph}
                />
              </div> */}
              <div className='flex gap-4'>
                <p>{m.dialog_include_updated()}</p>
                <Switch
                  checked={columnPreferences['Updated At']}
                  onCheckedChange={(v) => {
                    setColumnPreferences({
                      ...columnPreferences,
                      'Updated At': v,
                    });
                  }}
                />
              </div>
              <div className='flex gap-4'>
                <p>{m.dialog_include_created()}</p>
                <Switch
                  checked={columnPreferences['Created At']}
                  onCheckedChange={(v) => {
                    setColumnPreferences({
                      ...columnPreferences,
                      'Created At': v,
                    });
                  }}
                />
              </div>
              <div className='flex gap-4'>
                <p>{m.dialog_include_timestamp()}</p>
                <Switch
                  checked={columnPreferences.Timestamp}
                  onCheckedChange={(v) => {
                    setColumnPreferences({
                      ...columnPreferences,
                      Timestamp: v,
                    });
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </DialogContent>
  );
}

const useNotesWithMetadata = (
  notes: ReadonlyArray<Note>,
  enabledColumns: Set<PossibleColumn>,
) => {
  const columnConfig: Record<
    PossibleColumn,
    (n: Note) => string | Promise<string>
  > = useMemo(
    () => ({
      Title: (n: Note) => n.title,
      Body: (n: Note) => n.body,
      'Created At': (n: Note) => format(n.created_at_ms ?? Date.now(), 'PPPpp'),
      'Updated At': (n: Note) => format(n.updated_at_ms ?? Date.now(), 'PPPpp'),
      Timestamp: (n: Note) => formatNoteLocation(n),
      // Paragraph: async (n: Note) => {
      //   const timestamps = await getTimestampsForNote(n);
      //   if (!timestamps) return '';
      //   if (n.chapter_audio_offset_ms == undefined) return '';
      //   const offset = n.chapter_audio_offset_ms;
      //   const paragraphIdx = timestamps.paragraphs.findIndex(
      //     (p) =>
      //       p.timestamp_ms &&
      //       p.timestamp_ms.start <= offset &&
      //       p.timestamp_ms.end >= offset,
      //   );
      //   return (paragraphIdx + 1).toString();
      // },
      // Page: async (n: Note) => {
      //   const timestamps = await getTimestampsForNote(n);
      //   if (!timestamps) return '';
      //   if (!n.chapter_audio_offset_ms) return '';
      //   const offset = n.chapter_audio_offset_ms;
      //   const paragraph = timestamps.paragraphs.find(
      //     (p) =>
      //       p.timestamp_ms &&
      //       p.timestamp_ms.start <= offset &&
      //       p.timestamp_ms.end >= offset,
      //   );
      //   return (
      //     paragraph?.sentences[0]?.words[0]?.bboxes[0]?.page.toString() ?? ''
      //   );
      // },
    }),
    [],
  );

  const [isLoading, setIsLoading] = useState(true);
  const [notesWithMetaData, setNotesWithMetaData] = useState<
    Array<Partial<Record<PossibleColumn, string>>>
  >([]);

  useEffect(() => {
    const getMetaData = async () => {
      setIsLoading(true);
      const notesWithMeta = await Promise.all(
        notes.map(async (n) => {
          const formattedNote: Partial<Record<PossibleColumn, string>> = {};
          for (const col of enabledColumns) {
            const value = await columnConfig[col](n);
            formattedNote[col] = value;
          }
          return formattedNote;
        }),
      );
      setNotesWithMetaData(notesWithMeta);
      setIsLoading(false);
    };
    void getMetaData();
  }, [columnConfig, enabledColumns, notes]);

  return { isLoading, notesWithMetaData };
};
